import { useApolloClient } from '@apollo/client'
import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { Checkbox } from 'components/v2/Checkbox'
import EmptyState from 'components/v2/EmptyState'
import FormLayout from 'features/App/components/FormLayout'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { FaresList } from './FaresList'
import { resolvers as loginResolvers } from 'resolvers/login'
import { schema } from './schema'
import { getUnprocessableErrors } from 'utils/error'
import { device } from 'utils/style'
import { GTSegmentSuggestion } from 'gql/types.generated'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import { BothWay, OneWay, Pointer, SplitColumn } from 'components/SelectOriginCityAndDestinationCity'
import { useAllSegmentsByCities } from 'features/Segments/hooks'
import { MultiSegmentAutocomplete } from 'features/Segments/components/MultiSegmentAutocomplete'

const FormContent = styled(FormLayout.CardContent)`
  max-width: none;
  margin: 0 20px;
`

const FormFooter = styled(FormLayout.CardFooter)`
  max-width: none;
  margin: 0 10%;
`

const ErrorMessageWrapper = styled.div<TSFixMe>`
  text-align: right;
`

const CheckboxWrapper = styled.div<TSFixMe>`
  @media ${device.laptop} {
    margin-top: 35px;
  }
`

const Split = styled.div<TSFixMe>`
  width: 100%;
  height: 1px;
  background-color: #9998c0;
`

const messages = defineMessages({
  companyPlaceholder: {
    id: 'fareFamilies.new.company.placeholder',
    defaultMessage: 'Select company'
  },
  segmentsPlaceholder: {
    id: 'fareFamilies.new.segments.placeholder',
    defaultMessage: 'Select segments'
  },
  isReverseLabel: {
    id: 'fareFamilies.form.isReverse.label',
    defaultMessage: 'Add reverse'
  },
  originCityPlaceholder: {
    id: 'fareFamilies.form.originCity.placeholder',
    defaultMessage: 'Select an origin city'
  },
  destinationCityPlaceholder: {
    id: 'fareFamilies.form.destinationCity.placeholder',
    defaultMessage: 'Select a destination city'
  }
})

const NewFareFamilyForm = (props: TSFixMe): TSFixMe => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setValues,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    handleSubmit,
    error,
    loading,
    canEditCompany,
    isEdit
  } = props

  const client = useApolloClient()
  client.addResolvers(loginResolvers)
  const intl = useIntl()

  const { segmentSuggestions, loading: segmentsLoading } = useAllSegmentsByCities({
    companyId: values.company.id,
    cityOrigins: [values.originCity],
    cityDestinations: [values.destinationCity],
    isBothWays: values.areCitiesBothWay,
    onlyNoFareFamily: true
  })

  const handleCompanyChange = (value: TSFixMe): TSFixMe => {
    setValues({
      ...values,
      company: value,
      segments: [],
      fares: [],
      allSegmentsAreSelected: false
    })
  }

  const handleOriginCityChange = (city: TSFixMe): void => {
    setValues({
      ...values,
      originCity: city,
      segments: [],
      allSegmentsAreSelected: false
    })
  }

  const handleDestinationCityChange = (city: TSFixMe): void => {
    setValues({
      ...values,
      destinationCity: city,
      segments: [],
      allSegmentsAreSelected: false
    })
  }

  const handleSegmentsChange = (segments: TSFixMe): TSFixMe => {
    setFieldValue('segments', segments)
  }

  const handleSelectAllSegmentsChange = async ({
    target: { checked }
  }: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    let segments: GTSegmentSuggestion[] = []

    if (checked) {
      segments = segmentSuggestions
    }

    setValues({
      ...values,
      allSegmentsAreSelected: checked,
      segments
    })
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormContent>
        <Row>
          <Column width={`${100 / 4}%`}>
            <Input
              id="familyName"
              defaultValue="B"
              label={<FormattedMessage id="dictionary.familyName" defaultMessage="Family Name" />}
              readOnly
            />
          </Column>
          <Column width={`${100 / 4}%`}>
            <CompanyAutocomplete
              id="company.id"
              onChange={handleCompanyChange}
              onBlur={handleBlur}
              value={values.company.name}
              placeholder={intl.formatMessage(messages.companyPlaceholder)}
              touched={touched.company && touched.company.id}
              errors={errors.company && errors.company.id}
              label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
              readOnly={!canEditCompany}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <CityAutocomplete
              id="originCity.id"
              label={<FormattedMessage id="dictionary.originCity" defaultMessage="Origin City" />}
              value={values.originCity.name}
              onChange={handleOriginCityChange}
              placeholder={intl.formatMessage(messages.originCityPlaceholder)}
            />
          </Column>
          <SplitColumn noMargin>
            <Pointer
              onClick={async (): Promise<void> => {
                setValues({
                  ...values,
                  areCitiesBothWay: !values.areCitiesBothWay,
                  segments: [],
                  allSegmentsAreSelected: false
                })
              }}
            >
              {values.areCitiesBothWay ? <BothWay /> : <OneWay />}
            </Pointer>
          </SplitColumn>
          <Column>
            <CityAutocomplete
              id="destinatioCity.id"
              label={<FormattedMessage id="dictionary.destinationCity" defaultMessage="Destination City" />}
              value={values.destinationCity.name}
              onChange={handleDestinationCityChange}
              placeholder={intl.formatMessage(messages.destinationCityPlaceholder)}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Checkbox
              id="loyalty.campaing.rule.new.selectAll.id"
              checked={values.allSegmentsAreSelected}
              onChange={handleSelectAllSegmentsChange}
              disabled={!values.originCity.id || !values.destinationCity.id || segmentsLoading}
            >
              <FormattedMessage id="inventoryRule.new.applyOnAllSegment.label" defaultMessage="Apply on all segments" />
            </Checkbox>
          </Column>
        </Row>
        <Row>
          <Column width="75%">
            <MultiSegmentAutocomplete
              onChange={handleSegmentsChange}
              onBlur={handleBlur}
              values={values.segments}
              placeholder={intl.formatMessage(messages.segmentsPlaceholder)}
              touched={touched.segments}
              errors={errors.segments}
              label={<FormattedMessage id="dictionary.segments" defaultMessage="Segments" />}
              companyId={values.company?.id}
              originIds={values.originCity?.id ? [values.originCity.id] : []}
              destinationIds={values.destinationCity?.id ? [values.destinationCity.id] : []}
              onlyNoFareFamily
              disabled={values.allSegmentsAreSelected || segmentsLoading}
              skip={!values.company.id}
            />
          </Column>
          <Column width={`${100 / 4}%`}>
            <CheckboxWrapper>
              <Checkbox id="isReverse" checked={values.isReverse} onChange={handleChange}>
                {intl.formatMessage(messages.isReverseLabel)}
              </Checkbox>
            </CheckboxWrapper>
          </Column>
        </Row>
        <Row>
          <Column>
            <Split />
          </Column>
        </Row>
        <Row>
          <Column>
            {values.company.id ? (
              <FaresList
                values={values}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                handleBlur={handleBlur}
                touched={touched}
                errors={errors}
                company={values.company}
              />
            ) : (
              <EmptyState.Container>
                <EmptyState.Description>
                  <FormattedMessage
                    id="fareFamilies.form.emptyCompany.desc"
                    defaultMessage="You need to select a company before adding fares"
                  />
                </EmptyState.Description>
              </EmptyState.Container>
            )}
          </Column>
        </Row>
      </FormContent>
      <FormLayout.Split />
      <FormFooter>
        {error && (
          <ErrorMessageWrapper>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="fareFamilies.form.error"
                defaultMessage="There was an error while creating your fare family"
              />
            </ErrorMessage>
          </ErrorMessageWrapper>
        )}
        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/fare-families" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormFooter>
    </form>
  )
}

export const NewFareFamily = ({ canEditCompany, onSubmit, error, loading, isEdit }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const initialFareFamily = {
    company: { id: '', name: '' },
    familyName: 'B',
    segments: [],
    isReverse: true,
    fares: [],
    originCity: { id: '', name: '' },
    destinationCity: { id: '', name: '' },
    areCitiesBothWay: false,
    allSegmentsAreSelected: false
  }

  return (
    <Formik initialValues={initialFareFamily} validationSchema={schema(intl, canEditCompany)} onSubmit={onSubmit}>
      {(props): TSFixMe => {
        return (
          <NewFareFamilyForm
            {...props}
            isEdit={isEdit}
            canEditCompany={canEditCompany}
            error={error}
            loading={loading}
          />
        )
      }}
    </Formik>
  )
}
